import {
  Home, Book, Package, Users, Image, ClipboardList,
  RotateCw, Lightbulb, Tag, Layers, ChevronDown, ChevronRight, Search,
  Settings, User, Gift, Percent, Bell, MapPin, Pen, LogOut, Truck, Database
} from "lucide-react";
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { adminLogout } from "../../features/adminAuthSlice";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const navRef = useRef(null);
  const scrollPositionRef = useRef(0);

  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem("sidebar-collapsed");
    return stored ? JSON.parse(stored) : false;
  });
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Load expanded groups from localStorage, or default to all collapsed
    const stored = localStorage.getItem("sidebar-expanded-groups");
    return stored ? JSON.parse(stored) : {};
  });
  const [searchQuery, setSearchQuery] = useState("");

  const role = useSelector(state => {
    const roles = state.adminAuth.admin?.roles;
    if (!roles) return "";
    if (Array.isArray(roles)) {
      const first = roles[0];
      if (typeof first === "string") return first;
      if (first && typeof first.name === "string") return first.name;
    }
    return state.adminAuth.admin?.role || "";
  });

  const admin = useSelector((state) => state.adminAuth.admin);

  const menuGroups = useMemo(() => [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <Home size={18} />,
      path: "/adminDashboard",
      roles: null,
    },
    {
      id: "books",
      label: "Book Management",
      icon: <Book size={18} />,
      roles: ["ROLE_SUPER_ADMIN", "ROLE_EDITOR"],
      children: [
        { name: "All Books", icon: <Book size={16} />, path: "/bookManager", activePaths: ["/bookManager", "/bookForm"] },
        { name: "Categories", icon: <Layers size={16} />, path: "/categoryManager" },
        { name: "Authors", icon: <Pen size={16} />, path: "/authorManager" },
        { name: "Book Suggestions", icon: <Lightbulb size={16} />, path: "/book-suggestions", roles: ["ROLE_SUPER_ADMIN"] },
        { name: "Book Donations", icon: <Gift size={16} />, path: "/book-donations", roles: ["ROLE_SUPER_ADMIN"] },
      ],
    },
    {
      id: "users",
      label: "Users & Subscribers",
      icon: <Users size={18} />,
      roles: ["ROLE_SUPER_ADMIN"],
      children: [
        { name: "All Users", icon: <User size={16} />, path: "/user-manager" },
        { name: "Active Subscribers", icon: <Users size={16} />, path: "/subscribed-users" },
        { name: "Subscription Plans", icon: <Package size={16} />, path: "/packageManager" },
        { name: "Expired Subscribers", icon: <Users size={16} />, path: "/expired-subscribers" },
      ],
    },
    {
      id: "orders",
      label: "Orders",
      icon: <ClipboardList size={18} />,
      roles: null,
      children: [
        { name: "Book Order Requests", icon: <ClipboardList size={16} />, path: "/adminOrderRequests" },
        { name: "Return Requests", icon: <RotateCw size={16} />, path: "/adminReturnRequests" },
        { name: "Book Order History", icon: <Package size={16} />, path: "/book-order-history" },
      ],
    },
    {
      id: "service",
      label: "Service Management",
      icon: <MapPin size={18} />,
      roles: ["ROLE_SUPER_ADMIN"],
      children: [
        { name: "City Manager", icon: <MapPin size={16} />, path: "/city-manager" },
        { name: "Delivery Management", icon: <Truck size={16} />, path: "/delivery-manage" },
      ],
    },
    {
      id: "website",
      label: "Website",
      icon: <Image size={18} />,
      roles: ["ROLE_SUPER_ADMIN"],
      children: [
        { name: "Banner Management", icon: <Image size={16} />, path: "/bannerManager" },
        { name: "Testimonials", icon: <Users size={16} />, path: "/testimonial-manager" },
        { name: "Offers", icon: <Tag size={16} />, path: "/offer-manager" },
        { name: "Coupons", icon: <Percent size={16} />, path: "/coupon-manager" },
        { name: "Notifications", icon: <Bell size={16} />, path: "/notification-manager" },
      ],
    },
    {
      id: "system",
      label: "System",
      icon: <Settings size={18} />,
      roles: ["ROLE_SUPER_ADMIN"],
      children: [
        { name: "Admin Users", icon: <Users size={16} />, path: "/admin-users" },
        { name: "Activity Log", icon: <ClipboardList size={16} />, path: "/activity-log" },
        { name: "Database Backup", icon: <Database size={16} />, path: "/database-backup" },
        { name: "Investor Enquiries", icon: <Users size={16} />, path: "/investor-enquiries" },
      ],
    },
  ], []);

  // Auto-expand the group containing the current active route
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Find which group contains the current path
    const activeGroup = menuGroups.find(group => {
      if (group.path === currentPath) return true;
      if (group.children) {
        return group.children.some(child => {
          if (child.path === currentPath) return true;
          return child.activePaths?.some(p => currentPath.startsWith(p));
        });
      }
      return false;
    });

    // If we found an active group with children, expand only that group (close all others)
    if (activeGroup && activeGroup.children && activeGroup.id) {
      setExpandedGroups(() => {
        const newState = { [activeGroup.id]: true };
        localStorage.setItem("sidebar-expanded-groups", JSON.stringify(newState));
        return newState;
      });
    }
  }, [location.pathname, menuGroups]);

  // Save scroll position when user scrolls
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    const handleScroll = () => {
      scrollPositionRef.current = navElement.scrollTop;
    };

    navElement.addEventListener('scroll', handleScroll);
    return () => navElement.removeEventListener('scroll', handleScroll);
  }, []); // Only set up once

  // Restore scroll position after group expansion changes
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    // Small delay to allow DOM to update after expansion/collapse
    const timeoutId = setTimeout(() => {
      navElement.scrollTop = scrollPositionRef.current;
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [expandedGroups]); // Only restore when groups expand/collapse

  const handleNavigation = (path) => {
    navigate(path);
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      // If clicking on already expanded group, collapse it
      if (prev[groupId]) {
        const newState = {};
        localStorage.setItem("sidebar-expanded-groups", JSON.stringify(newState));
        return newState;
      }
      // Otherwise, collapse all and expand only the clicked group
      const newState = { [groupId]: true };
      localStorage.setItem("sidebar-expanded-groups", JSON.stringify(newState));
      return newState;
    });
  };

  const isActive = (item) => {
    if (location.pathname === item.path) return true;
    return item.activePaths?.some(p => location.pathname.startsWith(p));
  };

  const handleLogout = () => {
    dispatch(adminLogout());
    navigate("/admin");
  };

  const filterMenuItems = (groups) => {
    if (!searchQuery) return groups;
    
    return groups.map(group => {
      if (group.children) {
        const filteredChildren = group.children.filter(child =>
          child.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (filteredChildren.length > 0) {
          return { ...group, children: filteredChildren };
        }
      }
      if (group.label.toLowerCase().includes(searchQuery.toLowerCase())) {
        return group;
      }
      return null;
    }).filter(Boolean);
  };

  const filteredGroups = filterMenuItems(menuGroups);

  return (
    <aside
      className={`
        fixed md:static z-50 
        ${collapsed ? "w-16" : "w-64"}
        h-screen
        bg-white dark:bg-gray-900
        border-r border-gray-200 dark:border-gray-800
        transition-all duration-300 
        flex flex-col
      `}
    >
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <img 
            src="/images/Logo.svg" 
            alt="Books@Doorsteps Logo" 
            className="w-40 h-32 object-contain dark:hidden"
          />
          <img 
            src="/images/adminLogo.png" 
            alt="Books@Doorsteps Logo" 
            className="w-40 h-32 object-contain hidden dark:block"
          />
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 transition"
          aria-label="Toggle sidebar"
        >
          <ChevronRight size={18} className={`transition-transform ${collapsed ? "" : "rotate-180"}`} />
        </button>
      </div>

      {/* Search */}
      {!collapsed && (
        <div className="p-3 border-b border-gray-200 dark:border-gray-800">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-gray-900 dark:text-white placeholder-gray-400"
            />
          </div>
        </div>
      )}

      {/* Menu List */}
      <nav ref={navRef} className="flex-1 overflow-y-auto p-3 space-y-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {filteredGroups
          .filter(group => !group.roles || group.roles.includes(role))
          .map(group => {
            if (!group.children) {
              // Single item (Dashboard)
              const active = location.pathname === group.path;
              return (
                <button
                  key={group.id}
                  onClick={() => handleNavigation(group.path)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all
                    ${active
                      ? "bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }
                    ${collapsed ? "justify-center" : ""}
                  `}
                >
                  {group.icon}
                  {!collapsed && <span>{group.label}</span>}
                </button>
              );
            }

            // Group with children
            const isExpanded = expandedGroups[group.id];
            return (
              <div key={group.id}>
                <button
                  onClick={() => !collapsed && toggleGroup(group.id)}
                  className={`
                    w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-normal transition-all duration-200
                    text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800
                    ${collapsed ? "justify-center" : "justify-between"}
                  `}
                >
                  <div className="flex items-center gap-2.5">
                    {group.icon}
                    {!collapsed && <span>{group.label}</span>}
                  </div>
                  {!collapsed && (
                    <ChevronRight 
                      size={14} 
                      className={`text-gray-400 transition-transform duration-300 ease-in-out ${isExpanded ? 'rotate-90' : ''}`} 
                    />
                  )}
                </button>

                {/* Children */}
                {!collapsed && (
                  <div 
                    className={`
                      overflow-hidden transition-all duration-300 ease-in-out
                      ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                    `}
                  >
                    <div className="ml-4 mt-1 space-y-1 border-l border-gray-200 dark:border-gray-800 pl-3">
                      {group.children
                        .filter(child => !child.roles || child.roles.includes(role))
                        .map(child => {
                          const active = isActive(child);
                          return (
                            <button
                              key={child.name}
                              onClick={() => handleNavigation(child.path)}
                              className={`
                                w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all duration-150
                                ${active
                                  ? "bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 font-medium"
                                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200"
                                }
                              `}
                            >
                              {child.icon}
                              <span>{child.name}</span>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </nav>

      {/* Bottom Section */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-800 space-y-1">
        {!collapsed && (
          <div className="px-3 py-2 mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-xs font-semibold">
                {admin?.name?.charAt(0) || "A"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{admin?.name || "Admin"}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{role.replace("ROLE_", "")}</p>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`
            w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all
            text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10
            ${collapsed ? "justify-center" : ""}
          `}
        >
          <LogOut size={18} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
